import React, { useEffect, useMemo, useState } from 'react'
import useItemsDB from '../../hooks/useItemsDB'
import useCategories from '../../hooks/useCategories'
import { useCreateSalesOrder } from '../../hooks/useSalesOrder'
import useCart from '../../hooks/useCart' // ✅ integrated new hook
import usePermissions from '../../hooks/usePermissions'
import toast, { Toaster } from 'react-hot-toast'
import addcart from '../../assets/images/Cart Icon.png'
import Loader from "../../components/common/GlobalLoader"

function formatMoney(value) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value)
}

const Categories = () => {
  const customerName = localStorage.getItem('user')
    ? JSON.parse(localStorage.getItem('user')).CustomerName
    : null

  const { items: apiData, loading, error } = useItemsDB(200);
  const { createSalesOrder, loading: creating, error: createError } = useCreateSalesOrder()
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories()
  const { addToCart, loading: cartLoading } = useCart() // ✔ useCart
  const { canViewPrices, canCreateSalesOrders } = usePermissions()

  const [rows, setRows] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOption, setSortOption] = useState("")
  const itemsPerPage = 15

  const [showConfirmPopup, setShowConfirmPopup] = useState(false)
  const [orderPreviewItems, setOrderPreviewItems] = useState([])

  const isLoading = loading || creating || categoriesLoading || cartLoading

  const visibleCategoryNames = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r?.category) set.add(r.category);
    });
    return set;
  }, [rows]);

  useEffect(() => {
    if (apiData?.length > 0) {
      const activeItems = apiData.filter(
        (item) => !item.status || item.status?.toLowerCase() === "active"
      );

      const formatted = activeItems.map((item) => {
        const stock = parseFloat(item.stock_on_hand || 0);
        return {
          originalId: item.item_id,
          code: item.item_id?.slice(-4) || '----',
          product: item.name,
          unit: item.unit || '',
          stock: stock <= 0 ? 'Low Stock' : stock >= 50 ? '50+' : `${stock}`,
          stockValue: stock,
          price: parseFloat(item.rate || 0),
          incVat: parseFloat((item.rate || 0) * 1.2).toFixed(2),
          category: item.category_name || 'Uncategorized',
          qty: 0,
        };
      });
      setRows(formatted);
    }
  }, [apiData]);

  const updateQty = (index, delta) => {
    setRows(prev =>
      prev.map((r, i) => i === index ? { ...r, qty: Math.max(0, r.qty + delta) } : r)
    )
  }

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    )
    setCurrentPage(1)
  }

  let filteredRows = rows.filter(r => {
    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.some(cat => cat.toLowerCase().trim() === r.category.toLowerCase().trim())

    const matchesSearch =
      searchTerm === "" ||
      r.product.toLowerCase().includes(searchTerm.toLowerCase().trim())

    return matchesCategory && matchesSearch
  })

  if (sortOption) {
    filteredRows = [...filteredRows].sort((a, b) => {
      switch (sortOption) {
        case "name-asc": return a.product.localeCompare(b.product)
        case "name-desc": return b.product.localeCompare(a.product)
        case "price-asc": return a.price - b.price
        case "price-desc": return b.price - a.price
        case "stock-asc": return (a.stockValue || 0) - (b.stockValue || 0)
        case "stock-desc": return (b.stockValue || 0) - (a.stockValue || 0)
        case "category-asc": return a.category.localeCompare(b.category)
        case "category-desc": return b.category.localeCompare(a.category)
        default: return 0
      }
    })
  }

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedRows = filteredRows.slice(startIndex, startIndex + itemsPerPage)

  const handleOpenConfirmPopup = () => {
    const itemsToOrder = rows.filter(r => r.qty > 0)
    if (itemsToOrder.length === 0) {
      alert("Please select at least one item with quantity greater than 0.")
      return
    }
    setOrderPreviewItems(itemsToOrder)
    setShowConfirmPopup(true)
  }

  const handleConfirmOrder = async () => {
    const payloadItems = orderPreviewItems.map(item => ({
      item_id: item.originalId || '256873000149972193',
      quantity: item.qty
    }))

    const success = await createSalesOrder(payloadItems, orderPreviewItems.map(i => i.qty))

    if (success) {
      setRows(prev => prev.map(r => ({ ...r, qty: 0 })))
      toast.success("Sales order created successfully!")
      setShowConfirmPopup(false)
    } else {
      toast.error("Failed to create sales order.")
    }
  }

  if (error) return <p>Error loading products: {error}</p>
  if (categoriesError) return <p>Error loading categories: {categoriesError}</p>

  const totalAmount = orderPreviewItems.reduce((acc, item) => acc + (item.price * item.qty), 0)

  return (
    <div className="relative">

      {/* ✅ UPDATED LOADER — no fullscreen overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg z-40">
          <Loader />
        </div>
      )}

      <div className={`bg-white border rounded-xl ${isLoading ? 'opacity-30 pointer-events-none select-none' : ''}`}>
        <Toaster position="top-right" />

        {/* Header */}
        <div className="p-4 sm:p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
          <div className="text-sm text-gray-600 text-center sm:text-left">
            Displaying <span className="font-medium">{filteredRows.length}</span> products
          </div>

          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 w-full sm:w-auto">

            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              className="border rounded-md px-3 py-2 text-sm w-full sm:w-48 md:w-56 focus:outline-none focus:ring-1 focus:ring-gray-400"
            />

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm w-full sm:w-48 md:w-52 focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option value="">Sort by...</option>
              <option value="name-asc">Name (A → Z)</option>
              <option value="name-desc">Name (Z → A)</option>
              <option value="price-asc">Price (Low → High)</option>
              <option value="price-desc">Price (High → Low)</option>
              <option value="stock-asc">Stock (Low → High)</option>
              <option value="stock-desc">Stock (High → Low)</option>
              <option value="category-asc">Category (A → Z)</option>
              <option value="category-desc">Category (Z → A)</option>
            </select>

            <div className="relative w-full sm:w-auto">
              <details className="group">
                <summary className="px-3 py-2 border rounded-md text-sm cursor-pointer select-none w-full sm:w-auto text-center sm:text-left">
                  {selectedCategories.length > 0 ? `${selectedCategories.length} Selected` : "Filter Categories"}
                </summary>
                <div className="absolute left-0 right-0 sm:left-auto sm:right-0 mt-2 w-full sm:w-56 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto z-10 p-2">
                  {categories?.length > 0 ? (
                    categories
                      .filter((cat) => visibleCategoryNames.has(cat.name))
                      .map(cat => (
                      <label
                        key={cat.category_id || cat.document_id}
                        className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat.name)}
                          onChange={() => toggleCategory(cat.name)}
                          className="form-checkbox"
                        />
                        {cat.name}
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 px-2 py-1">No categories found</p>
                  )}
                </div>
              </details>
            </div>

          </div>
        </div>

        {/* Table Header */}
        <div className="border-t border-b bg-gray-50 text-[13px] sm:text-sm font-medium text-gray-700 overflow-x-auto">
          <div
            className="
              min-w-[900px] 
              grid grid-cols-[70px_1fr_150px_100px_100px_120px_180px]
              sm:grid-cols-[80px_1fr_160px_110px_110px_130px_200px]
              px-4 sm:px-6 py-3 gap-3 items-center
            "
          >
            <div className="text-center sm:text-left">Image</div>
            <div>Item Name</div>
            <div>Category</div>
            <div className="text-center">Unit</div>
            <div className="text-center">Stock</div>
            {canViewPrices && <div className="text-center">Price</div>}
            <div className="text-center">Quantity / Action</div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y overflow-x-auto">
          {paginatedRows.map((r, i) => (
            <div
              key={r.code + i}
              className="
                min-w-[900px] 
                grid grid-cols-[70px_1fr_150px_100px_100px_120px_180px]
                sm:grid-cols-[80px_1fr_160px_110px_110px_130px_200px]
                px-4 sm:px-6 py-3 gap-3 items-center text-[13px] sm:text-sm
              "
            >
              <div className="flex justify-center sm:justify-start">
                <img
                  src="https://i.ibb.co/xqk7vzMk/download.png"
                  alt="Product"
                  className="h-9 w-9 sm:h-10 sm:w-10 object-contain rounded-md border"
                />
              </div>

              <div className="font-medium text-gray-900 break-words">{r.product}</div>
              <div className="text-gray-700">{r.category}</div>
              <div className="text-center text-gray-700">{r.unit}</div>

              <div
                className={`text-center font-medium ${r.stock === "Low Stock" ? "text-red-600" : "text-gray-700"}`}
              >
                {r.stock}
              </div>

              {canViewPrices && (
                <div className="text-center font-semibold text-gray-900">
                  {formatMoney(r.price)}
                </div>
              )}

              <div className="flex items-center justify-center gap-3 sm:gap-4 w-full flex-wrap">
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <button
                    onClick={() => updateQty(rows.indexOf(r), -1)}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-md border text-gray-600 hover:bg-gray-50"
                  >
                    −
                  </button>

                  <input
                    type="number"
                    min="0"
                    value={r.qty === 0 ? "" : r.qty}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === "") {
                        setRows(prev =>
                          prev.map((row, idx) =>
                            idx === rows.indexOf(r) ? { ...row, qty: 0 } : row
                          )
                        )
                        return
                      }
                      const newQty = parseInt(value, 10)
                      if (!isNaN(newQty) && newQty >= 0) {
                        setRows(prev =>
                          prev.map((row, idx) =>
                            idx === rows.indexOf(r) ? { ...row, qty: newQty } : row
                          )
                        )
                      }
                    }}
                    className="h-7 sm:h-8 w-12 sm:w-14 border rounded-md text-center text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />

                  <button
                    onClick={() => updateQty(rows.indexOf(r), 1)}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-md border text-primary hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>

                {canCreateSalesOrders ? (
                  <img
                    src={addcart}
                    alt="Add to Cart"
                    onClick={async () => {
                      if (r.qty <= 0) {
                        toast.error("Please set a quantity before adding to cart.")
                        return
                      }
                      try {
                        await addToCart(r.originalId, r.qty)
                        toast.success(`${r.product} added to cart!`)

                        setRows(prev =>
                          prev.map(row =>
                            row.originalId === r.originalId ? { ...row, qty: 0 } : row
                          )
                        )

                      } catch (err) {
                        toast.error("Failed to add item to cart.")
                      }
                    }}
                    className="h-6 w-6 sm:h-7 sm:w-7 cursor-pointer hover:scale-110 transition-transform"
                  />
                ) : (
                  <div className="text-xs text-gray-500 text-center px-2">
                    Contact support
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="px-3 sm:px-4 md:px-6 py-4 flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm text-gray-700 border-t gap-2">
          <div>
            {currentPage > 1 && (
              <button
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                « Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 flex-wrap justify-center">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1
              if (pageNum > 5) return null
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 py-1 rounded ${currentPage === pageNum ? "bg-gray-200 font-medium" : "hover:bg-gray-50"}`}
                >
                  {pageNum}
                </button>
              )
            })}
            {totalPages > 5 && <span className="px-1">...</span>}
          </div>

          <div>
            {currentPage < totalPages && (
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                Next »
              </button>
            )}
          </div>
        </div>

        {showConfirmPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-lg p-4 sm:p-6 shadow-lg max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg sm:text-xl font-semibold mb-4">Confirm Sales Order</h2>

              <div className="mb-3 text-gray-700 text-sm sm:text-base">
                <div><strong>Customer Name:</strong> {customerName}</div>
                <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm border-collapse mb-4">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2">Item</th>
                      <th className="text-center py-2">Qty</th>
                      {canViewPrices && (
                        <>
                          <th className="text-right py-2">Price</th>
                          <th className="text-right py-2">Amount</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {orderPreviewItems.map(item => (
                      <tr key={item.originalId} className="border-b border-gray-200">
                        <td className="py-2">{item.product}</td>
                        <td className="text-center py-2">{item.qty}</td>
                        {canViewPrices && (
                          <>
                            <td className="text-right py-2">{formatMoney(item.price)}</td>
                            <td className="text-right py-2">{formatMoney(item.price * item.qty)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {canViewPrices && (
                <div className="text-right font-semibold mb-4">
                  Total: {formatMoney(totalAmount)}
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={() => setShowConfirmPopup(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100 w-full sm:w-auto"
                >
                  Cancel
                </button>

                {canCreateSalesOrders ? (
                  <button
                    onClick={handleConfirmOrder}
                    disabled={creating}
                    className="bg-[#33080f] text-white px-4 py-2 rounded hover:bg-[#490c15] disabled:opacity-50 w-full sm:w-auto"
                  >
                    {creating ? "Creating Order..." : "Confirm Order"}
                  </button>
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded border border-gray-300 w-full sm:w-auto text-center">
                    Contact support to purchase items
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default Categories
